import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Stack,
  IconButton,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useState, useCallback, useMemo } from 'react';
import MainCard from 'ui-component/cards/MainCard';
import { useGetPurchaseRequestGroupDetail } from 'views/simplifiedPurchasing/hooks/useGetPurchaseRequestGroupDetail';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import ExtendedDatagrid from 'ui-component/DataGrid/ExtendedDatagrid';
import { makeBuyableColumns } from 'views/simplifiedPurchasing/Cart/columns';
import { DatagridNames, RowAction } from 'types/datagrid';
import { V2OrgPartsFieldName } from 'ui-component/DataGrid/PartV2ColumnDef';
import { HookFormInput } from 'ui-component/HookFormComponents';
import { handleErr } from 'utils/functions';
import { useTypedForm } from 'hooks/useTypedForm';
import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import {
  PurchaseRequestSchema,
  PurchaseRequestSchemaLite,
  PurchaseRequestGroupSchema,
} from 'types/clientV2/purchaseRequests';
import Hero from 'ui-component/Hero';
import EditInline from 'ui-component/EditInline';
import {
  useUpdatePurchaseRequestGroupsMutation,
  useDeletePurchaseRequestGroupsMutation,
  useDeletePurchaseRequestsMutation,
  useApprovePurchaseRequestGroupsActionMutation,
} from 'store/slices/clientV2/purchaseRequests';
import { ALL_APPS, ALL_VIEWS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { IconTrash, IconUser, IconUsers } from '@tabler/icons-react';
import DeleteRecordDialog from 'ui-component/clientV2/DeleteRecordDialog';
import useSnackbar from 'hooks/useSnackbar';
import { gridSpacing } from 'constants/themeConstants';
import { HookFormQuillInput } from 'ui-component/HookFormComponents/HookFormQuillInput';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';
import {
  formConstants,
  validationSchema,
} from 'views/simplifiedPurchasing/RFQCart/constants';
import InfoSectionItem, {
  InfoSectionItemFormatTypes,
} from 'ui-component/InfoSectionItem';
import { DASHBOARD_ROUTE } from 'routes/constants';
import WithSkeleton from 'ui-component/extended/WithSkeleton';

const RFQCart = () => {
  const { purchaseRequestGroupId } = useParams<'purchaseRequestGroupId'>();
  const navigate = useNavigate();
  const { purchaseRequestGroup, purchaseRequests, isLoading, isFetching } =
    useGetPurchaseRequestGroupDetail(purchaseRequestGroupId);
  const { dispatchErrorSnackbar } = useSnackbar();
  const { data: currentUser } = useGetUserQuery(undefined);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePurchaseRequestDialogOpen, setDeletePurchaseRequestDialogOpen] =
    useState(false);
  const [approvePurchaseRequestGroup] =
    useApprovePurchaseRequestGroupsActionMutation();
  const [selectedPurchaseRequestId, setSelectedPurchaseRequestId] = useState<
    string | null
  >(null);
  const [isSubmittingRfq, setIsSubmittingRfq] = useState(false);
  const [updatePurchaseRequestGroup, { isLoading: isUpdatingShared }] =
    useUpdatePurchaseRequestGroupsMutation();

  const { handleSubmit } = useTypedForm(validationSchema, {
    defaultValues: {
      [formConstants.notes.id]: '',
    },
  });

  const tempUoM = useFeature(FeatureFlags.tempUoM).on;

  const isCreator = purchaseRequestGroup?.requestedBy?.id === currentUser?.id;

  const handleToggleShared = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newSharedValue: boolean | null) => {
      if (
        purchaseRequestGroup?.id &&
        newSharedValue !== null &&
        (newSharedValue === true || isCreator)
      ) {
        updatePurchaseRequestGroup({
          id: purchaseRequestGroup.id,
          shared: newSharedValue,
        });
      }
    },
    [purchaseRequestGroup?.id, isCreator, updatePurchaseRequestGroup]
  );
  const handleSubmitRfq = handleSubmit(async (data) => {
    if (!purchaseRequestGroup?.id) {
      return;
    }
    setIsSubmittingRfq(true);
    try {
      await approvePurchaseRequestGroup({
        ids: [purchaseRequestGroup.id],
      }).unwrap();
      const path = `${ALL_VIEWS.SOURCING_RFQS.path}/${purchaseRequestGroup.id}`;
      if (path) {
        navigate(path);
      }
    } catch (error) {
      handleErr(error, (errMessage: string) => {
        dispatchErrorSnackbar(errMessage);
      });
    } finally {
      setIsSubmittingRfq(false);
    }
  });

  const handleDeletePurchaseRequest = useCallback(
    (row?: PurchaseRequestSchemaLite) => {
      if (!row) {
        return;
      }
      setSelectedPurchaseRequestId(row.id);
      setDeletePurchaseRequestDialogOpen(true);
    },
    []
  );

  const rowActions: RowAction<PurchaseRequestSchemaLite>[] = useMemo(
    () => [
      {
        getLabel: () => 'Delete',
        Icon: IconTrash,
        color: 'error',
        getRowTooltip: () => 'Remove from cart',
        permissionScope: {
          app: ALL_APPS.SOURCING.id,
          accessLevel: appPermissionAccessLevels.edit,
        },
        onRowClick: handleDeletePurchaseRequest,
      },
    ],
    [handleDeletePurchaseRequest]
  );

  const columns = useMemo(
    () =>
      makeBuyableColumns({
        tempUoM,
      }),
    [tempUoM]
  );

  const purchaseRequestCount = purchaseRequests.length;

  return (
    <>
      <Grid container spacing={3}>
        {/* RFQ Name Header */}
        <Grid size={12}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Hero
              isLoading={isLoading}
              options={{
                middle: {
                  value: purchaseRequestGroup?.name ?? '',
                  clipboard: 'RFQ Name',
                  clipboardValue: purchaseRequestGroup?.name ?? '',
                  editInline: (
                    <EditInline
                      useMutation={useUpdatePurchaseRequestGroupsMutation}
                      name="name"
                      label="RFQ Name"
                      submitAsClientV2Api
                      value={purchaseRequestGroup?.name}
                      id={purchaseRequestGroup?.id || ''}
                      permissionScope={{
                        app: ALL_APPS.SOURCING.id,
                        accessLevel: appPermissionAccessLevels.edit,
                      }}
                    >
                      <HookFormInput disableRequiredProps type="text" />
                    </EditInline>
                  ),
                },
              }}
            />
            {purchaseRequestGroup?.id && (
              <Stack direction="row" spacing={1} alignItems="center">
                <WithSkeleton
                  isLoading={isLoading || isUpdatingShared || isFetching}
                  height={35}
                >
                  <ToggleButtonGroup
                    value={purchaseRequestGroup?.shared ?? false}
                    exclusive
                    onChange={handleToggleShared}
                    size="small"
                    disabled={isLoading || isUpdatingShared}
                  >
                    <Tooltip
                      title={
                        !isCreator
                          ? 'Only the RFQ creator can make it private'
                          : 'Make RFQ private'
                      }
                    >
                      <span>
                        <ToggleButton value={false} disabled={!isCreator}>
                          <IconUser size="1rem" />
                          <Typography>Private</Typography>
                        </ToggleButton>
                      </span>
                    </Tooltip>
                    <ToggleButton value>
                      <IconUsers size="1rem" />
                      <Typography>Public</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </WithSkeleton>
                <Tooltip title="Delete RFQ">
                  <IconButton
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={isLoading}
                    size="small"
                  >
                    <IconTrash stroke={1.5} size="1.3rem" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        </Grid>
        <Grid size={7}>
          <Stack spacing={3}>
            <MainCard loading={isLoading}>
              <ExtendedDatagrid
                loading={isLoading}
                editable
                exportFileNamePrefix="RFQ-Parts"
                noRows="No parts found"
                gridName={DatagridNames.rfqParts}
                rows={purchaseRequests}
                columns={columns}
                rowActions={rowActions}
                initialVisibleColumns={[
                  V2OrgPartsFieldName,
                  'quant',
                  'unitPrice',
                  'lineTotal',
                ]}
              />
            </MainCard>
          </Stack>
        </Grid>
        <Grid size={5}>
          <MainCard loading={isLoading} contentSX={{ px: 0 }}>
            <Stack spacing={gridSpacing}>
              <Stack
                direction="column"
                spacing={gridSpacing}
                sx={{
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  px: gridSpacing,
                }}
              >
                <Typography variant="body1">
                  Theses requests will automatically be sent to matching
                  suppliers in the Cofactr distributor network including the
                  part manufacturer (when direct quoting is available),
                  authorized distributors, and brokers. If your Need By Date is
                  more than 6 weeks in the future, we will prioritize quoting
                  via authorized channels before contacting brokers. If you have
                  specific supplier preferences, please enter those in the notes
                  fields.
                </Typography>
                {purchaseRequestGroup?.id && (
                  <InfoSectionItem
                    caption={formConstants.notes.label}
                    value={purchaseRequestGroup.internalNotes || ''}
                    formatType={InfoSectionItemFormatTypes.richText}
                    isLoading={isLoading}
                    placeholder="-"
                    permissionScope={{
                      app: ALL_APPS.SOURCING.id,
                      accessLevel: appPermissionAccessLevels.edit,
                    }}
                    editInline={
                      <EditInline
                        useMutation={useUpdatePurchaseRequestGroupsMutation}
                        name="internalNotes"
                        label={formConstants.notes.label}
                        submitAsClientV2Api
                        value={purchaseRequestGroup.internalNotes}
                        id={purchaseRequestGroup.id}
                        nullable
                        fullHeight
                        permissionScope={{
                          app: ALL_APPS.SOURCING.id,
                          accessLevel: appPermissionAccessLevels.edit,
                        }}
                      >
                        <HookFormQuillInput disableRequiredProps hideLabel />
                      </EditInline>
                    }
                  />
                )}
                <SegmentLoadingButton
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={
                    isLoading || isSubmittingRfq || purchaseRequestCount === 0
                  }
                  onClick={handleSubmitRfq}
                  loading={isSubmittingRfq}
                >
                  {isSubmittingRfq ? 'Submitting RFQ...' : 'Submit RFQ'}
                </SegmentLoadingButton>
              </Stack>
            </Stack>
          </MainCard>
        </Grid>
      </Grid>
      {purchaseRequestGroup?.id && (
        <DeleteRecordDialog<PurchaseRequestGroupSchema>
          id={purchaseRequestGroup.id}
          dialogOpen={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          recordName="RFQ"
          useClientDeleteMutation={useDeletePurchaseRequestGroupsMutation}
          message="Are you sure you want to delete this RFQ? This action cannot be undone."
          onSuccess={() => {
            navigate(DASHBOARD_ROUTE);
          }}
        />
      )}
      {selectedPurchaseRequestId && (
        <DeleteRecordDialog<PurchaseRequestSchema>
          id={selectedPurchaseRequestId}
          dialogOpen={deletePurchaseRequestDialogOpen}
          onClose={() => {
            setDeletePurchaseRequestDialogOpen(false);
            setSelectedPurchaseRequestId(null);
          }}
          recordName="Line"
          useClientDeleteMutation={useDeletePurchaseRequestsMutation}
          message="Are you sure you want to remove this part from the RFQ? This action cannot be undone."
        />
      )}
    </>
  );
};

export default RFQCart;
